'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { reportEvent } from '@/app/utils';
import { normalizePath } from '../../../_utils';
import type { ClawFileItem } from '../../../types';
import type { ActiveSelection, DisplayRow } from '../types';
import { getFolderKey, isFolder, sortItems } from '../utils';

const REFRESH_DEPTH_LIMIT = 3;

interface ListFolderResult {
  data: ClawFileItem[] | null;
  error: unknown;
}

interface UseFileExplorerOptions {
  activeClawId?: string;
  refreshKey: number;
  listFolder: (options: { folderPath?: string; pageSize?: number }) => Promise<ListFolderResult>;
  /**
   * 树视图展开的根节点路径（不显示根节点自身，仅显示其子项）。默认 '' 即整个 claw。
   */
  rootPath?: string;
  /**
   * 初始化时需要展开并选中的目标路径，必须位于 rootPath 之下，否则忽略。
   */
  initialPath?: string;
}

const getParentFolderPath = (path: string): string => {
  const segments = normalizePath(path).split('/').filter(Boolean);
  segments.pop();
  return segments.join('/');
};

const isDescendantPath = (rootPath: string, targetPath: string): boolean => {
  if (!targetPath) return false;
  if (!rootPath) return true;
  if (targetPath === rootPath) return true;
  return targetPath.startsWith(`${rootPath}/`);
};

const getDescendantSegments = (rootPath: string, targetPath: string): string[] => {
  if (!isDescendantPath(rootPath, targetPath) || targetPath === rootPath) return [];
  const remainder = rootPath ? targetPath.slice(rootPath.length + 1) : targetPath;
  const parts = remainder.split('/').filter(Boolean);
  const segments: string[] = [];
  let acc = rootPath;
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    segments.push(acc);
  }
  return segments;
};

export const useFileExplorer = ({
  activeClawId,
  refreshKey,
  listFolder,
  rootPath,
  initialPath,
}: UseFileExplorerOptions) => {
  const normalizedRootPath = normalizePath(rootPath);
  const t = useTranslations();
  const [folderItemsMap, setFolderItemsMap] = useState<Record<string, ClawFileItem[]>>({});
  const [expandedFolderMap, setExpandedFolderMap] = useState<Record<string, boolean>>({});
  const [loadingFolderMap, setLoadingFolderMap] = useState<Record<string, boolean>>({});
  const [currentFolderPath, setCurrentFolderPath] = useState('');
  const [activeSelection, setActiveSelection] = useState<ActiveSelection>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [previewFilePath, setPreviewFilePath] = useState('');
  const handledRefreshKeyRef = useRef(refreshKey);
  const initialPathRef = useRef(initialPath);
  useEffect(() => {
    initialPathRef.current = initialPath;
  }, [initialPath]);

  const refreshFolder = useCallback(
    async (folderPath?: string): Promise<ClawFileItem[]> => {
      const normalizedPath = normalizePath(folderPath);
      const key = getFolderKey(normalizedPath);

      setLoadingFolderMap((prev) => ({ ...prev, [key]: true }));
      const { data, error } = await listFolder({ folderPath: normalizedPath, pageSize: 50 });
      setLoadingFolderMap((prev) => ({ ...prev, [key]: false }));

      if (error || !data) {
        toast({ type: 'error', message: t('claw.file.listFailed') });
        return [];
      }

      const nextItems = sortItems(data);
      setFolderItemsMap((prev) => ({
        ...prev,
        [key]: nextItems,
      }));
      return nextItems;
    },
    [listFolder, t],
  );

  const refreshFolderTree = useCallback(
    async (folderPath: string, depth: number): Promise<void> => {
      const items = await refreshFolder(folderPath);

      if (depth >= REFRESH_DEPTH_LIMIT) {
        return;
      }

      const childFolders = items.filter((item) => isFolder(item) && normalizePath(item.path));
      if (!childFolders.length) {
        return;
      }

      await Promise.all(childFolders.map((item) => refreshFolderTree(normalizePath(item.path), depth + 1)));
    },
    [refreshFolder],
  );

  const refreshCurrentFolder = useCallback(async (): Promise<void> => {
    await refreshFolder(currentFolderPath);
  }, [currentFolderPath, refreshFolder]);

  const refreshFolders = useCallback(
    async (paths: string[]): Promise<void> => {
      const uniquePaths = Array.from(new Map(paths.map((path) => [getFolderKey(path), path])).values());
      await Promise.all(uniquePaths.map((path) => refreshFolder(path)));
    },
    [refreshFolder],
  );

  useEffect(() => {
    if (!activeClawId) return;

    const init = async () => {
      setCurrentFolderPath(normalizedRootPath);
      setActiveSelection(null);
      setPreviewFilePath('');
      setFolderItemsMap({});
      setExpandedFolderMap({});
      setInitialLoading(true);
      handledRefreshKeyRef.current = refreshKey;
      await refreshFolder(normalizedRootPath);

      const targetPath = normalizePath(initialPathRef.current);
      if (targetPath && targetPath !== normalizedRootPath && isDescendantPath(normalizedRootPath, targetPath)) {
        const segments = getDescendantSegments(normalizedRootPath, targetPath);
        for (const segment of segments) {
          setExpandedFolderMap((prev) => ({ ...prev, [getFolderKey(segment)]: true }));
          await refreshFolder(segment);
        }
        setCurrentFolderPath(targetPath);
        setActiveSelection({ type: 'folder', path: targetPath });
      }

      setInitialLoading(false);
    };

    init();
  }, [activeClawId, normalizedRootPath]);

  // initialPath 在使用过程中可能被外部刷新（例如极速模式首次发消息后建好新目录、
  // 或上传 md 后定位到 story 目录）。init 完成后再次到达新的子路径时，
  // 这里负责把树展开并把选中态对齐到目标，避免用户还停在 root。
  useEffect(() => {
    if (!activeClawId) return;
    if (initialLoading) return;
    const targetPath = normalizePath(initialPath);
    if (!targetPath || targetPath === normalizedRootPath) return;
    if (!isDescendantPath(normalizedRootPath, targetPath)) return;
    if (targetPath === currentFolderPath) return;

    let cancelled = false;
    const sync = async () => {
      const segments = getDescendantSegments(normalizedRootPath, targetPath);
      for (const segment of segments) {
        if (cancelled) return;
        setExpandedFolderMap((prev) =>
          prev[getFolderKey(segment)] ? prev : { ...prev, [getFolderKey(segment)]: true },
        );
        await refreshFolder(segment);
      }
      if (cancelled) return;
      setCurrentFolderPath(targetPath);
      setActiveSelection({ type: 'folder', path: targetPath });
    };
    void sync();
    return () => {
      cancelled = true;
    };
  }, [activeClawId, initialLoading, initialPath, normalizedRootPath, currentFolderPath, refreshFolder]);

  useEffect(() => {
    if (!activeClawId || initialLoading || refreshKey === handledRefreshKeyRef.current) {
      return;
    }

    handledRefreshKeyRef.current = refreshKey;
    refreshCurrentFolder();
  }, [activeClawId, initialLoading, refreshCurrentFolder, refreshKey]);

  const displayRows = useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    const visited = new Set<string>();

    const walk = (folderPath: string, depth: number) => {
      const folderKey = getFolderKey(folderPath);
      const items = folderItemsMap[folderKey] ?? [];

      for (const item of items) {
        rows.push({ item, depth });

        const itemPath = normalizePath(item.path);
        const itemKey = getFolderKey(itemPath);
        if (!isFolder(item) || !expandedFolderMap[itemKey] || visited.has(itemKey)) continue;

        visited.add(itemKey);
        walk(itemPath, depth + 1);
      }
    };

    walk(normalizedRootPath, 0);
    return rows;
  }, [expandedFolderMap, folderItemsMap, normalizedRootPath]);

  const activeFile = useMemo<ClawFileItem | null>(() => {
    if (!previewFilePath) return null;

    for (const items of Object.values(folderItemsMap)) {
      const target = items.find((item) => !isFolder(item) && normalizePath(item.path) === previewFilePath);
      if (target) return target;
    }

    return null;
  }, [folderItemsMap, previewFilePath]);

  const handleToggleFolder = useCallback(
    async (item: ClawFileItem): Promise<void> => {
      const itemPath = normalizePath(item.path);
      if (!itemPath) return;

      reportEvent('folder_click', { folder_name: itemPath.split('/').filter(Boolean).pop() ?? itemPath });

      setCurrentFolderPath(itemPath);
      setActiveSelection({ type: 'folder', path: itemPath });
      setPreviewFilePath('');

      const itemKey = getFolderKey(itemPath);
      const nextExpanded = !expandedFolderMap[itemKey];
      setExpandedFolderMap((prev) => ({ ...prev, [itemKey]: nextExpanded }));

      if (!nextExpanded || folderItemsMap[itemKey]) return;
      await refreshFolder(itemPath);
    },
    [expandedFolderMap, folderItemsMap, refreshFolder],
  );

  const handleSelectFolder = useCallback((item: ClawFileItem): void => {
    if (!isFolder(item)) return;

    const folderPath = normalizePath(item.path);
    if (!folderPath) return;

    reportEvent('folder_click', { folder_name: folderPath.split('/').filter(Boolean).pop() ?? folderPath });

    setCurrentFolderPath(folderPath);
    setPreviewFilePath('');
    setActiveSelection((prev) =>
      prev?.type === 'folder' && prev.path === folderPath ? null : { type: 'folder', path: folderPath },
    );
  }, []);

  const handleSelectFile = useCallback((item: ClawFileItem): void => {
    if (isFolder(item)) return;

    const filePath = normalizePath(item.path);
    if (!filePath) return;

    setActiveSelection({ type: 'file', path: filePath });
    setPreviewFilePath((prev) => (prev === filePath ? '' : filePath));
  }, []);

  const closePreview = useCallback((): void => {
    setPreviewFilePath('');
    setActiveSelection((prev) => (prev?.type === 'file' ? null : prev));
  }, []);

  /**
   * 按文件路径在树中定位并打开预览：
   * - 路径在 rootPath 之下：逐级展开父目录、加载父目录内容、校验文件存在后打开预览
   * - 路径不在 rootPath 之下：直接打开预览，不做树定位（与 Grid 行为对齐）
   */
  const openPreviewByPath = useCallback(
    async (targetFilePath: string): Promise<void> => {
      const normalizedTargetPath = normalizePath(targetFilePath);
      if (!normalizedTargetPath) return;

      if (!isDescendantPath(normalizedRootPath, normalizedTargetPath)) {
        setPreviewFilePath(normalizedTargetPath);
        setActiveSelection({ type: 'file', path: normalizedTargetPath });
        return;
      }

      if (normalizedTargetPath === normalizedRootPath) return;

      const parentFolderPath = getParentFolderPath(normalizedTargetPath);
      const segments = getDescendantSegments(normalizedRootPath, parentFolderPath);

      let parentItems: ClawFileItem[] = [];
      for (const segment of segments) {
        setExpandedFolderMap((prev) => ({ ...prev, [getFolderKey(segment)]: true }));
        const items = await refreshFolder(segment);
        if (segment === parentFolderPath) {
          parentItems = items;
        }
      }
      if (segments.length === 0) {
        parentItems = await refreshFolder(parentFolderPath);
      }

      const matched = parentItems.find((item) => !isFolder(item) && normalizePath(item.path) === normalizedTargetPath);
      if (!matched) {
        toast({ type: 'error', message: t('claw.file.fileDeleted') });
        return;
      }

      setCurrentFolderPath(parentFolderPath);
      setActiveSelection({ type: 'file', path: normalizedTargetPath });
      setPreviewFilePath(normalizedTargetPath);
    },
    [normalizedRootPath, refreshFolder],
  );

  const getActionTargetFolderPath = useCallback((): string => {
    if (activeSelection?.type === 'folder') return activeSelection.path;
    if (activeSelection?.type === 'file') return getParentFolderPath(activeSelection.path);
    return currentFolderPath;
  }, [activeSelection, currentFolderPath]);

  const handleDeleteSuccess = useCallback(
    (item: ClawFileItem): string[] => {
      const itemPath = normalizePath(item.path);
      if (!itemPath) {
        return [];
      }

      const deletingFolder = isFolder(item);
      const isDeletingCurrentFolder = deletingFolder && currentFolderPath === itemPath;
      const rawFallback = isDeletingCurrentFolder ? getParentFolderPath(itemPath) : '';
      const fallbackFolderPath = isDeletingCurrentFolder
        ? normalizedRootPath && !isDescendantPath(normalizedRootPath, rawFallback)
          ? normalizedRootPath
          : rawFallback
        : '';
      const nextFolderPath = isDeletingCurrentFolder ? fallbackFolderPath : currentFolderPath;

      setActiveSelection((prev) => {
        if (!prev) return prev;
        if (prev.type === 'file' && prev.path === itemPath) return null;
        if (prev.type === 'folder' && prev.path === itemPath) {
          return isDeletingCurrentFolder && fallbackFolderPath ? { type: 'folder', path: fallbackFolderPath } : null;
        }
        return prev;
      });

      setPreviewFilePath((prev) => (prev === itemPath ? '' : prev));

      if (isDeletingCurrentFolder) {
        setCurrentFolderPath(fallbackFolderPath);
      }

      return [normalizedRootPath, nextFolderPath];
    },
    [currentFolderPath, normalizedRootPath],
  );

  return {
    folderItemsMap,
    expandedFolderMap,
    loadingFolderMap,
    currentFolderPath,
    activeSelection,
    initialLoading,
    previewFilePath,
    activeFile,
    displayRows,
    refreshFolder,
    refreshFolderTree,
    refreshCurrentFolder,
    refreshFolders,
    handleToggleFolder,
    handleSelectFolder,
    handleSelectFile,
    closePreview,
    openPreviewByPath,
    getActionTargetFolderPath,
    handleDeleteSuccess,
  };
};
